import { useCallback, useRef, useState } from 'react';

interface DropzoneProps {
    acceptedFileTypes: string[];
    maxUploadBytes: number;
    disabled?: boolean;
    onFile: (file: File) => void;
}

/** Drag & drop / click uploader (successor of the legacy DragAndDrop component). */
export function Dropzone({ acceptedFileTypes, maxUploadBytes, disabled, onFile }: DropzoneProps) {
    const [dragging, setDragging] = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);

    const accept = acceptedFileTypes.map((ext) => `.${ext}`).join(',');
    const maxMb = Math.floor(maxUploadBytes / (1024 * 1024));

    const handleFiles = useCallback(
        (files: FileList | null) => {
            const file = files?.item(0);
            if (file) {
                onFile(file);
            }
        },
        [onFile],
    );

    return (
        <div
            className={`dropzone${dragging ? ' dropzone--active' : ''}${disabled ? ' dropzone--disabled' : ''}`}
            onDragOver={(event) => {
                event.preventDefault();
                if (!disabled) setDragging(true);
            }}
            onDragLeave={() => setDragging(false)}
            onDrop={(event) => {
                event.preventDefault();
                setDragging(false);
                if (!disabled) handleFiles(event.dataTransfer.files);
            }}
            onClick={() => !disabled && inputRef.current?.click()}
            onKeyDown={(event) => {
                if (!disabled && (event.key === 'Enter' || event.key === ' ')) {
                    event.preventDefault();
                    inputRef.current?.click();
                }
            }}
            role="button"
            tabIndex={0}
            aria-disabled={disabled}
            aria-label="Upload a track for mastering"
        >
            <input
                ref={inputRef}
                type="file"
                className="dropzone__input"
                accept={accept}
                disabled={disabled}
                onChange={(event) => {
                    handleFiles(event.target.files);
                    event.target.value = '';
                }}
            />

            <div className="dropzone__icon" aria-hidden="true">
                <svg viewBox="0 0 24 24" width="44" height="44" fill="none" stroke="currentColor" strokeWidth="1.6">
                    <path d="M12 16V4m0 0L7.5 8.5M12 4l4.5 4.5" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M20 15v3a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2v-3" strokeLinecap="round" />
                </svg>
            </div>

            <p className="dropzone__title">Drop your track here</p>
            <p className="dropzone__hint">
                or click to browse — {acceptedFileTypes.join(', ').toUpperCase()} up to {maxMb} MB
            </p>
        </div>
    );
}
